import { ReactNode } from "react";
import { When } from "react-if";
import { DateTime } from "luxon";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  currencyFormatter,
  displayPhoneNumber,
  IContactPerson,
  IEscalationResponseItem,
  langauge,
  PolicyEscalationType,
  toDecimal,
} from "shared";

import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { DatePopover } from "../../../common/Components/DatePopover";
import { NotesDrawerResource } from "../../slices/notesDrawer";
import { useContact } from "../../hooks/useContact";
import { useInvoice } from "../../hooks/useInvoice";
import { useCurrentEscalation } from "../../hooks/useCurrentEscalation";
import { useGetSelectedOrganisation } from "../../hooks/useGetSelectedOrganisation";
import { WrappedLink } from "../WrappedLink";
import { getEscalationDisplayStatus } from "../Escalations/EscalationTable";

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className={ `text-gray-500` }>{ label }</dt>
      <dd className={ `min-w-0 text-gray-700 font-medium` }>{ children }</dd>
    </>
  );
}

function DetailList({ children }: { children: ReactNode }) {
  return (
    <dl className={ `grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-sm` }>
      { children }
    </dl>
  );
}

function PersonRow({ person, isPrimary }: { person: IContactPerson; isPrimary: boolean }) {
  const name = [person.firstName, person.lastName].filter(Boolean).join(` `) || `(No name found)`;
  const mobile = displayPhoneNumber(person.mobileCountryCode, person.mobileNumber);
  const phone = displayPhoneNumber(person.defaultCountryCode, person.defaultNumber);

  return (
    <li className={ `py-2` }>
      <Paragraph>
        { name }
        <When condition={ isPrimary }>
          <span className={ `ml-2 text-xs font-normal text-gray-400` }>{ `Primary` }</span>
        </When>
      </Paragraph>
      <When condition={ !!person.email }>
        <Paragraph
          variant={ `secondary` }
          className={ `truncate` }
          tooltipped
        >
          { person.email }
        </Paragraph>
      </When>
      <When condition={ !!mobile }>
        <Paragraph variant={ `secondary` }>{ `Mobile: ${mobile}` }</Paragraph>
      </When>
      <When condition={ !!phone }>
        <Paragraph variant={ `secondary` }>{ `Phone: ${phone}` }</Paragraph>
      </When>
    </li>
  );
}

function ContactSummary({ contactId }: { contactId: number }) {
  const { data: contact } = useContact(contactId);

  if (!contact) return null;

  return (
    <details
      open
      className={ `group` }
    >
      <summary className={ `flex cursor-pointer list-none items-center justify-between gap-2` }>
        <div className={ `min-w-0` }>
          <WrappedLink to={ `/contacts/${contact.id}` }>
            <Paragraph
              variant={ `link` }
              as={ `span` }
              size={ `md` }
            >
              { contact.name }
            </Paragraph>
          </WrappedLink>
          <When condition={ !!contact.externalAccountNumber }>
            <Paragraph variant={ `help` }>{ contact.externalAccountNumber }</Paragraph>
          </When>
        </div>
        <ChevronDownIcon className={ `h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-180` } />
      </summary>

      <ul className={ `divide-y divide-gray-100` }>
        <PersonRow
          person={ contact.primaryContactPerson }
          isPrimary
        />
        { contact.contactPersons.map((person, index) => (
          <PersonRow
            key={ person.id || index }
            person={ person }
            isPrimary={ false }
          />
        )) }
      </ul>
    </details>
  );
}

/** Shared by invoice and escalation summaries, both carry the raw invoice row */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function InvoiceDetails({ invoice }: { invoice: any }) {
  const currentOrg = useGetSelectedOrganisation();
  const format = currencyFormatter(invoice.xero_currency);

  let daysOverdue: number | null = null;
  if (invoice.xero_status === `AUTHORISED` && invoice.due_date_string && currentOrg?.currentTime) {
    const diffMs = new Date(currentOrg.currentTime).getTime() - new Date(invoice.due_date_string).getTime();
    daysOverdue = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  }

  return (
    <>
      <Detail label={ `Invoice` }>
        <WrappedLink to={ `/invoices/${invoice.id}` }>
          <Paragraph
            variant={ `link` }
            as={ `span` }
          >
            { invoice.xero_number }
          </Paragraph>
        </WrappedLink>
      </Detail>
      <Detail label={ `Invoice Status` }>{ invoice.xero_status }</Detail>
      <Detail label={ `Total` }>{ format(toDecimal(invoice.xero_amount_total || 0)) }</Detail>
      <Detail label={ `Amount Due` }>{ format(toDecimal(invoice.xero_amount_due || 0)) }</Detail>
      <Detail label={ `Issue Date` }>{ invoice.issue_date_string }</Detail>
      <Detail label={ `Due Date` }>
        { invoice.due_date_string }
        <When condition={ daysOverdue !== null && daysOverdue > 0 }>
          <span className={ `ml-1 text-red-600` }>{ `(${daysOverdue} days overdue)` }</span>
        </When>
      </Detail>
    </>
  );
}

function InvoiceSummary({ invoiceId }: { invoiceId: string }) {
  const { invoice } = useInvoice(invoiceId);

  if (!invoice) return null;

  return (
    <>
      <ContactSummary contactId={ invoice.contact_id } />
      <DetailList>
        <InvoiceDetails invoice={ invoice } />
      </DetailList>
    </>
  );
}

function EscalationDetails({ escalation }: { escalation: IEscalationResponseItem }) {
  const currentOrg = useGetSelectedOrganisation();
  const assignee = escalation.assignedUser
    ? `${escalation.assignedUser.firstName || ``} ${escalation.assignedUser.lastName || ``}`.trim() || escalation.assignedUser.email
    : `Unassigned`;

  return (
    <DetailList>
      <Detail label={ `Type` }>
        { langauge.escalation.type[escalation.escalationType as PolicyEscalationType]?.label || escalation.escalationType }
      </Detail>
      <Detail label={ `Policy` }>{ escalation.policyId ? escalation.policy?.title || `-` : `Manual` }</Detail>
      <Detail label={ `Status` }>{ getEscalationDisplayStatus(escalation) }</Detail>
      <Detail label={ `Assignee` }>{ assignee }</Detail>
      <Detail label={ `Scheduled` }>
        <DatePopover
          date={ escalation.escalationDate }
          labelFormatPreset={ DateTime.DATETIME_MED }
          organisationTimezone={ currentOrg?.validatedTimezone }
        />
      </Detail>
      { escalation.invoice && <InvoiceDetails invoice={ escalation.invoice } /> }
    </DetailList>
  );
}

function EscalationSummary({ escalationId }: { escalationId: number }) {
  const { currentEscalation } = useCurrentEscalation(escalationId);

  if (!currentEscalation) return null;

  return (
    <>
      <ContactSummary contactId={ currentEscalation.contactId } />
      <EscalationDetails escalation={ currentEscalation } />
    </>
  );
}

/** Contact details (and invoice / escalation details) shown above the notes in the notes drawer */
export function NotesDrawerSummary({ resource }: { resource: NotesDrawerResource }) {
  return (
    <div className={ `mb-4 space-y-3 rounded-md border border-gray-200 p-3` }>
      { resource.type === `contact` && <ContactSummary contactId={ resource.id } /> }
      { resource.type === `invoice` && <InvoiceSummary invoiceId={ resource.id } /> }
      { resource.type === `escalation` && <EscalationSummary escalationId={ resource.id } /> }
    </div>
  );
}
